import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Globe, Shield, Users, Calendar, BarChart3, Mail, Tag, Megaphone, Heart, Map, GraduationCap, FileText, Ticket, ChevronRight, Search, Copy, ExternalLink, Package, Building2, RefreshCw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SITE_MAP = [
  {
    section: 'Public Pages',
    icon: Globe,
    color: 'text-blue-400',
    description: 'Pages visible to all visitors & customers',
    pages: [
      { path: '/', label: 'Homepage / Landing Page', desc: 'Main marketing page' },
      { path: '/services', label: 'Services Showcase', desc: 'All services, filterable by city' },
      { path: '/denver', label: 'Denver Landing Page', desc: 'Denver lead capture & services' },
      { path: '/las-vegas', label: 'Las Vegas Landing Page', desc: 'Las Vegas lead capture & services' },
      { path: '/los-angeles', label: 'Los Angeles Landing Page', desc: 'LA lead capture & services' },
      { path: '/charlotte', label: 'Charlotte Landing Page', desc: 'Charlotte lead capture & services' },
      { path: '/plan', label: 'Plan Your Event', desc: 'Interactive event planner & cart' },
      { path: '/events', label: 'Events Listing', desc: 'Browse upcoming hosted events' },
      { path: '/blog', label: 'Blog', desc: 'SEO content & articles' },
      { path: '/partners/vendor', label: 'Vendor Partner Application', desc: 'Vendor signup page' },
      { path: '/careers/sales-agent', label: 'Sales Agent Application', desc: 'Sales agent recruitment' },
      { path: '/contact', label: 'Contact Us', desc: 'Contact form & info' },
    ]
  },
  {
    section: 'Admin Dashboard',
    icon: Shield,
    color: 'text-indigo-400',
    description: 'Core admin management tools',
    pages: [
      { path: '/admin/dashboard', label: 'Dashboard Home', desc: 'Overview, stats, quick actions' },
      { path: '/admin/pipeline', label: 'Pipeline / CRM', desc: 'Leads, proposals & invoices' },
      { path: '/admin/proposals/new', label: 'Proposal Generator', desc: 'Create proposals' },
      { path: '/admin/proposals/new?type=invoice', label: 'Invoice Generator', desc: 'Create invoices' },
      { path: '/admin/products', label: 'Products / Services', desc: 'Manage service catalog' },
      { path: '/admin/venues', label: 'Venue Partners', desc: 'Manage venue listings' },
      { path: '/admin/payments', label: 'Payments', desc: 'Payment plans & transactions' },
    ]
  },
  {
    section: 'Events & Ticketing',
    icon: Ticket,
    color: 'text-purple-400',
    description: 'Event management & ticketing system',
    pages: [
      { path: '/admin/events', label: 'Events Manager', desc: 'Create & manage hosted events' },
      { path: '/admin/ticket-holders', label: 'Ticket Holder CRM', desc: 'Attendee profiles, lists, email blasts' },
      { path: '/admin/promo-codes', label: 'Promo & Referral Codes', desc: 'Create & manage discount codes' },
    ]
  },
  {
    section: 'Marketing & Growth',
    icon: Megaphone,
    color: 'text-amber-400',
    description: 'Digital marketing tools',
    pages: [
      { path: '/admin/ad-creator', label: 'Digital Ad Creator', desc: 'AI ad campaigns, banners, email ads' },
      { path: '/admin/markets', label: 'Market Expansion', desc: 'City markets overview & stats' },
      { path: '/admin/blog', label: 'Blog Manager', desc: 'Create & manage blog posts' },
    ]
  },
  {
    section: 'Team & Sales',
    icon: Users,
    color: 'text-emerald-400',
    description: 'Sales agents & team management',
    pages: [
      { path: '/admin/sales-agents', label: 'Sales Agents', desc: 'Agent applications & management' },
      { path: '/sales', label: 'Sales Agent Portal', desc: 'Agent dashboard & tools' },
    ]
  },
  {
    section: 'Supplier Hub',
    icon: Calendar,
    color: 'text-rose-400',
    description: 'Vendor & supplier management',
    pages: [
      { path: '/supplier', label: 'Supplier Dashboard', desc: 'Vendor portal & calendar' },
      { path: '/admin/supplier', label: 'Admin Supplier Hub', desc: 'Manage all suppliers' },
    ]
  },
  {
    section: 'University & Training',
    icon: GraduationCap,
    color: 'text-cyan-400',
    description: 'Eventism University courses',
    pages: [
      { path: '/university', label: 'Eventism University', desc: 'Courses, tracks & leaderboard' },
    ]
  },
];

export default function SiteDirectory() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [live, setLive] = useState({ products: [], venues: [], events: [], blog_posts: [], marketing_pages: [], stats: {} });
  const [loadingLive, setLoadingLive] = useState(true);
  const [q, setQ] = useState('');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const loadLive = useCallback(async () => {
    setLoadingLive(true);
    try {
      const res = await axios.get(`${API}/admin/site-directory`, { headers });
      setLive(res.data);
    } catch (e) {
      // Non-blocking — the static map still renders. Only admins/agents can see this.
    }
    setLoadingLive(false);
  }, [headers]);

  useEffect(() => { loadLive(); }, [loadLive]);

  const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
  const fullUrl = (path) => `${baseUrl}${path}`;
  const copyLink = async (path, label) => {
    try {
      await navigator.clipboard.writeText(fullUrl(path));
      toast.success(`Link to "${label}" copied`);
    } catch (_) { toast.error('Could not copy'); }
  };

  // Search across both static map and live items
  const matches = (s) => !q || (s || '').toLowerCase().includes(q.toLowerCase());
  const filteredStatic = q ? SITE_MAP.map(sec => ({
    ...sec,
    pages: sec.pages.filter(p => matches(p.label) || matches(p.desc) || matches(p.path)),
  })).filter(sec => sec.pages.length > 0) : SITE_MAP;

  const liveSections = [
    { key: 'products', icon: Package, color: 'text-emerald-400', label: 'Service Products', desc: 'Live, customer-facing — share with leads', items: live.products, showImage: true },
    { key: 'venues', icon: Building2, color: 'text-indigo-400', label: 'Venues', desc: 'Live venue listings', items: live.venues, showImage: true },
    { key: 'events', icon: Ticket, color: 'text-purple-400', label: 'Hosted Events (ticketing)', desc: 'Published events with ticket pages', items: live.events, showImage: true },
    { key: 'blog_posts', icon: FileText, color: 'text-amber-400', label: 'Blog Posts', desc: 'Published articles', items: live.blog_posts },
    { key: 'marketing_pages', icon: Megaphone, color: 'text-rose-400', label: 'Marketing Pages', desc: 'Custom HTML page-builder pages', items: live.marketing_pages },
  ].map(sec => ({ ...sec, items: (sec.items || []).filter(it => matches(it.label) || matches(it.path) || matches(it.host)) }));

  return (
    <div className="min-h-screen bg-background pt-16 pb-12">
      <div className="max-w-5xl mx-auto px-4 sm:px-6">
        <div className="mb-6 pt-4 flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold">Site Directory</h1>
            <p className="text-sm text-muted-foreground">Every page on the Eventism platform — auto-updated as content lands. Click any item to open, or copy the link to share.</p>
          </div>
          <Button size="sm" variant="outline" onClick={loadLive} disabled={loadingLive} data-testid="dir-refresh">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${loadingLive ? 'animate-spin' : ''}`} /> Refresh
          </Button>
        </div>

        <div className="relative mb-5">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search a product name, page, blog post, or path…"
            className="pl-9"
            data-testid="dir-search-input"
          />
        </div>

        {/* Live, dynamically populated sections — these auto-update */}
        <div className="space-y-6">
          {liveSections.map(sec => {
            const Icon = sec.icon;
            // Always render the section so testids are stable; show an empty
            // placeholder when there's no live content yet.
            return (
              <Card key={sec.key} className="border-border" data-testid={`dir-live-${sec.key}`}>
                <CardContent className="p-5">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-9 h-9 rounded-lg bg-muted/50 flex items-center justify-center">
                      <Icon className={`h-5 w-5 ${sec.color}`} />
                    </div>
                    <div>
                      <h2 className="font-bold text-base">{sec.label}</h2>
                      <p className="text-xs text-muted-foreground">{sec.desc}</p>
                    </div>
                    <Badge variant="outline" className="ml-auto text-[10px]">
                      {loadingLive ? '…' : `${sec.items.length} live`}
                    </Badge>
                  </div>
                  {sec.items.length === 0 ? (
                    <p className="text-xs text-muted-foreground italic">No matches.</p>
                  ) : (
                    <div className="space-y-1.5">
                      {sec.items.slice(0, 50).map(it => (
                        <div
                          key={it.id || it.path}
                          className="flex items-center gap-3 p-2.5 rounded-lg border border-border/50 hover:border-indigo-500/30 hover:bg-indigo-500/5 transition-all group"
                          data-testid={`dir-item-${it.id}`}
                        >
                          {sec.showImage && it.image_url && (
                            <img src={it.image_url} alt="" className="w-10 h-10 rounded object-cover flex-shrink-0" />
                          )}
                          <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate(it.path)}>
                            <p className="text-sm font-medium group-hover:text-indigo-400 transition truncate">{it.label}</p>
                            <p className="text-[10px] text-muted-foreground truncate">
                              {it.host && <span>{it.host} · </span>}
                              {it.city && <span className="capitalize">{it.city}</span>}
                              {it.price ? <span> · ${it.price}</span> : null}
                              {it.start_date ? <span> · {new Date(it.start_date).toLocaleDateString()}</span> : null}
                            </p>
                            <code className="text-[9px] text-muted-foreground font-mono hidden sm:inline">{it.path}</code>
                          </div>
                          <Button size="sm" variant="ghost" onClick={() => copyLink(it.path, it.label)} title="Copy public link" data-testid={`copy-${it.id}`}>
                            <Copy className="w-3.5 h-3.5" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => window.open(it.path, '_blank')} title="Open in new tab">
                            <ExternalLink className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      ))}
                      {sec.items.length > 50 && (
                        <p className="text-[11px] text-muted-foreground text-center pt-2">Showing first 50 of {sec.items.length} — refine with search.</p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}

          {/* Static structural map — admin/marketing surfaces */}
          {filteredStatic.map(section => {
            const Icon = section.icon;
            return (
              <Card key={section.section} className="border-border">
                <CardContent className="p-5">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-9 h-9 rounded-lg bg-muted/50 flex items-center justify-center">
                      <Icon className={`h-5 w-5 ${section.color}`} />
                    </div>
                    <div>
                      <h2 className="font-bold text-base">{section.section}</h2>
                      <p className="text-xs text-muted-foreground">{section.description}</p>
                    </div>
                    <Badge variant="outline" className="ml-auto text-[10px]">{section.pages.length}</Badge>
                  </div>
                  <div className="grid sm:grid-cols-2 gap-1.5">
                    {section.pages.map(page => (
                      <div key={page.path} className="flex items-center gap-3 p-2.5 rounded-lg border border-border/50 hover:border-indigo-500/30 hover:bg-indigo-500/5 transition-all group" data-testid={`dir-${page.path.replace(/[/=?]/g, '-')}`}>
                        <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate(page.path)}>
                          <p className="text-sm font-medium group-hover:text-indigo-500 transition-colors">{page.label}</p>
                          <p className="text-[10px] text-muted-foreground">{page.desc}</p>
                        </div>
                        <code className="text-[9px] text-muted-foreground font-mono hidden sm:block">{page.path}</code>
                        <Button size="sm" variant="ghost" onClick={() => copyLink(page.path, page.label)} title="Copy">
                          <Copy className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
}
